<template>
  <div
    class="language"
    data-test="header-language"
    :class="{ 'language--open': dialogOpen }"
    @mouseenter="scheduleOpenLanguageDialog"
    @mouseleave="scheduleCloseLanguageDialog"
    @wheel="preventScrollWhenExpanded"
  >
    <button
      class="language-selection"
      :class="{ 'language-selection--open': dialogOpen }"
      type="button"
      data-test="header-link-language"
    >
      <span class="language-selection__label">{{ language }}</span>
      <PhCaretDown
        class="language-selection__icon"
        weight="bold"
        :size="14"
        aria-hidden="true"
      />
    </button>

    <LanguageDialog
      v-if="dialogOpen"
      @close-requested="closeLanguageDialog"
      class="language-selection-dialog"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import LanguageDialog from "@/components/header/LanguageDialog.vue";
import { useI18n } from "vue-i18n";
import { PhCaretDown } from "@phosphor-icons/vue";

const dialogOpen = ref(false);
const { locale: language } = useI18n();

const openDelay = 100;
const closeDelay = 100;

let openTimeout: ReturnType<typeof setTimeout> | undefined;
let closeTimeout: ReturnType<typeof setTimeout> | undefined;

const clearOpenTimeout = () => {
  if (openTimeout) {
    clearTimeout(openTimeout);
    openTimeout = undefined;
  }
};

const clearCloseTimeout = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = undefined;
  }
};

const scheduleOpenLanguageDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();

  openTimeout = setTimeout(() => {
    dialogOpen.value = true;
    openTimeout = undefined;
  }, openDelay);
};

const closeLanguageDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();
  dialogOpen.value = false;
};

const scheduleCloseLanguageDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();

  closeTimeout = setTimeout(() => {
    dialogOpen.value = false;
    closeTimeout = undefined;
  }, closeDelay);
};

onBeforeUnmount(() => {
  clearOpenTimeout();
  clearCloseTimeout();
});

watch(
  language,
  (value) => {
    document.querySelector("html")?.setAttribute("lang", value);
  },
  { immediate: true },
);

const preventScrollWhenExpanded = (event: WheelEvent) => {
  if (dialogOpen.value === true) {
    event.preventDefault();
  }
};
</script>

<style scoped lang="scss">
.language {
  position: relative;
  flex: 0 0 auto;
  height: 100%;
}

.language-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  width: 5.3rem;
  height: 100%;
  padding: 0;
  border: 0;
  background: white;
  color: $color-blue-dark;
  cursor: pointer;
  box-sizing: border-box;
}

.language-selection:hover,
.language-selection--open {
  background: #f3f4f7;
}

.language-selection--open .language-selection__icon {
  transform: rotate(180deg);
}

.language-selection__icon {
  color: currentColor;
  transition: transform 0.15s ease;
}

.language-selection__label {
  text-transform: uppercase;
  font-weight: 600;
  font-size: $font-size-h4;
  cursor: pointer;
}

.language-selection-dialog {
  position: absolute;
  top: 100%;
  left: 50%;
  z-index: 30;
  transform: translateX(-50%);
}
</style>
